import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import {
  getGetRolesNewQueryKey,
  useAssignRolesForUser,
  useRevokeRole,
} from "@vega-api/generated/auth-permission-v1/auth-permission-v1";
import { getGetWorkbenchCollaboratorsQueryKey } from "@vega-api/generated/auth-workbench-v1/auth-workbench-v1";
import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";
import { type CollaboratorPermissionEntry } from "@vega-api/generated/types/collaboratorPermissionEntry";
import { type ThemedDropdownOptionType } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import {
  ACTION_COLUMN_DEFAULT_PROPS,
  MAX_TEXT_COLUMN_PROPS,
} from "@vega-core/components/Table/CoreTable/constants";
import { m } from "@vega-core/paraglide/messages";
import { convertToTitleCase } from "@vega-core/utils/formatString";
import { isTruthy } from "@vega-core/utils/isTruthy";

import {
  type EditedPermission,
  FUND_VEHICLE_ORDER,
  FUND_VEHICLE_TAB,
  FUND_VEHICLE_TAB_TO_SUBJECT_TYPE,
  type FundVehicleTab,
  getFundVehicleTabFromPermission,
  getFundVehicleTag,
  getRoleOptionsForFundVehicleTab,
  type PendingRoleCreation,
} from "../components/collaboratorsV2Constants";

import {
  type CellState,
  DeleteActionCell,
  EditableFundVehicleCell,
  EditableRoleCell,
} from "./manageRolesCells";
import { useCollaboratorRoles } from "./useCollaboratorRoles";

// ─── Types ───────────────────────────────────────────────────────────────────

export type UserChangeState = {
  markedForDeletion: Set<string>;
  pendingCreations: PendingRoleCreation[];
  editedPermissions: Record<string, EditedPermission>;
};

const createEmptyUserState = (): UserChangeState => ({
  markedForDeletion: new Set(),
  pendingCreations: [],
  editedPermissions: {},
});

const createEmptyUserStateView = (): UserStateView => ({
  permissionsData: [],
  columns: [],
  collaboratorHasNoPermissions: true,
  hasChanges: false,
  hasInvalidEntries: false,
  markedForDeletion: new Set(),
  pendingCreations: [],
  editedPermissions: {},
  getDisabledRoleIdsForTab: () => [],
  getPermissionsMarkedForDeletion: () => [],
  addRole: () => {},
  toggleDeletion: () => {},
  unmarkForDeletion: () => {},
  removeRole: () => {},
  editPermission: () => {},
  resetChanges: () => {},
});

export type BulkSaveCallbacks = {
  onSuccess?: () => void;
  onPartialFailure?: (
    failed: { userId: string; error: ErrorType<unknown> }[],
  ) => void;
  onError?: (error: ErrorType<unknown>) => void;
};

export const getRowKey = (
  roleId: string | undefined,
  fundVehicleTab: FundVehicleTab | undefined,
): string => `${roleId ?? ""}:${fundVehicleTab ?? ""}`;

export const getRowKeyFromPermission = (
  permission: CollaboratorPermissionEntry,
): string =>
  getRowKey(permission.roleId, getFundVehicleTabFromPermission(permission));

// ─── Hook ────────────────────────────────────────────────────────────────────

export type UserStateView = {
  permissionsData: CollaboratorPermissionEntry[];
  columns: ColumnDef<CollaboratorPermissionEntry>[];
  collaboratorHasNoPermissions: boolean;
  hasChanges: boolean;
  hasInvalidEntries: boolean;
  markedForDeletion: Set<string>;
  pendingCreations: PendingRoleCreation[];
  editedPermissions: Record<string, EditedPermission>;
  getDisabledRoleIdsForTab: (
    tab: FundVehicleTab,
    excludeRowKey?: string,
  ) => string[];
  getPermissionsMarkedForDeletion: () => CollaboratorPermissionEntry[];
  addRole: (roleId: string, fundVehicleTab: FundVehicleTab) => void;
  toggleDeletion: (rowKey: string) => void;
  unmarkForDeletion: (rowKey: string) => void;
  removeRole: (rowKey: string) => void;
  editPermission: (rowKey: string, updates: Partial<EditedPermission>) => void;
  resetChanges: () => void;
};

export const useCollaboratorRolesManager = ({
  collaborators,
  isEditing,
}: {
  collaborators: CollaboratorDetails[];
  isEditing: boolean;
}) => {
  const queryClient = useQueryClient();
  const {
    getRoleById,
    roles: uniqueRoles,
    isLoading: isLoadingRoles,
  } = useCollaboratorRoles();
  const { mutateAsync: revokeRoleAsync, isPending: isDeleting } =
    useRevokeRole();
  const { mutateAsync: assignRolesAsync, isPending: isAssigning } =
    useAssignRolesForUser();

  const [userStates, setUserStates] = useState<
    Partial<Record<string, UserChangeState>>
  >({});
  const [failedUserIds, setFailedUserIds] = useState<Set<string>>(new Set());

  const collaboratorsById = useMemo(() => {
    const map = new Map<string, CollaboratorDetails>();
    collaborators.forEach((collaborator) =>
      map.set(collaborator.id, collaborator),
    );

    return map;
  }, [collaborators]);

  // ── Per-user state mutators ──

  const getOrInitUserState = (
    states: Partial<Record<string, UserChangeState>>,
    userId: string,
  ): UserChangeState => states[userId] ?? createEmptyUserState();

  const clearUserFailure = useCallback((userId: string) => {
    setFailedUserIds((previous) => {
      if (!previous.has(userId)) return previous;
      const next = new Set(previous);
      next.delete(userId);

      return next;
    });
  }, []);

  const toggleDeletion = useCallback(
    (userId: string, rowKey: string) => {
      setUserStates((previous) => {
        const current = getOrInitUserState(previous, userId);
        const nextMarked = new Set(current.markedForDeletion);

        if (nextMarked.has(rowKey)) {
          nextMarked.delete(rowKey);
        } else {
          nextMarked.add(rowKey);
        }

        return {
          ...previous,
          [userId]: { ...current, markedForDeletion: nextMarked },
        };
      });
      clearUserFailure(userId);
    },
    [clearUserFailure],
  );

  const unmarkForDeletion = useCallback(
    (userId: string, rowKey: string) => {
      setUserStates((previous) => {
        const current = getOrInitUserState(previous, userId);

        if (!current.markedForDeletion.has(rowKey)) return previous;
        const nextMarked = new Set(current.markedForDeletion);
        nextMarked.delete(rowKey);

        return {
          ...previous,
          [userId]: { ...current, markedForDeletion: nextMarked },
        };
      });
      clearUserFailure(userId);
    },
    [clearUserFailure],
  );

  const addRole = useCallback(
    (userId: string, roleId: string, fundVehicleTab: FundVehicleTab) => {
      setUserStates((previous) => {
        const current = getOrInitUserState(previous, userId);
        const isDuplicate = current.pendingCreations.some(
          (creation) =>
            creation.roleId === roleId &&
            creation.fundVehicleTab === fundVehicleTab,
        );

        if (isDuplicate) return previous;

        return {
          ...previous,
          [userId]: {
            ...current,
            pendingCreations: [
              ...current.pendingCreations,
              { roleId, fundVehicleTab },
            ],
          },
        };
      });
      clearUserFailure(userId);
    },
    [clearUserFailure],
  );

  const removeRole = useCallback(
    (userId: string, rowKey: string) => {
      setUserStates((previous) => {
        const current = getOrInitUserState(previous, userId);
        const filtered = current.pendingCreations.filter((creation) => {
          const creationRowKey = getRowKey(
            creation.roleId,
            creation.fundVehicleTab,
          );

          return creationRowKey !== rowKey;
        });

        if (filtered.length === current.pendingCreations.length)
          return previous;

        return {
          ...previous,
          [userId]: { ...current, pendingCreations: filtered },
        };
      });
      clearUserFailure(userId);
    },
    [clearUserFailure],
  );

  const editPermission = useCallback(
    (userId: string, rowKey: string, updates: Partial<EditedPermission>) => {
      setUserStates((previous) => {
        const current = getOrInitUserState(previous, userId);

        const pendingIndex = current.pendingCreations.findIndex(
          (creation) =>
            getRowKey(creation.roleId, creation.fundVehicleTab) === rowKey,
        );

        if (pendingIndex !== -1) {
          const existing = current.pendingCreations[pendingIndex];
          const nextPending = [...current.pendingCreations];
          nextPending[pendingIndex] = {
            roleId: updates.roleId ?? existing.roleId,
            fundVehicleTab: updates.fundVehicleTab ?? existing.fundVehicleTab,
          };

          return {
            ...previous,
            [userId]: { ...current, pendingCreations: nextPending },
          };
        }

        const collaborator = collaboratorsById.get(userId);
        const originalPermission = collaborator?.permissions?.find(
          (permission) => getRowKeyFromPermission(permission) === rowKey,
        );
        const seedRoleId = originalPermission?.roleId ?? "";
        const seedFundVehicleTab = originalPermission
          ? getFundVehicleTabFromPermission(originalPermission)
          : FUND_VEHICLE_TAB.ALL;

        return {
          ...previous,
          [userId]: {
            ...current,
            editedPermissions: {
              ...current.editedPermissions,
              [rowKey]: {
                ...(current.editedPermissions[rowKey] ?? {
                  roleId: seedRoleId,
                  fundVehicleTab: seedFundVehicleTab,
                }),
                ...updates,
              },
            },
          },
        };
      });
      clearUserFailure(userId);
    },
    [clearUserFailure, collaboratorsById],
  );

  const resetChanges = useCallback((userId?: string) => {
    setUserStates((previous) => {
      if (!userId) return {};

      if (!(userId in previous)) return previous;
      const { [userId]: _removed, ...rest } = previous;
      void _removed;

      return rest;
    });
    setFailedUserIds((previous) => {
      if (!userId) return new Set();

      if (!previous.has(userId)) return previous;
      const next = new Set(previous);
      next.delete(userId);

      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    setUserStates({});
    setFailedUserIds(new Set());
  }, []);

  // ── Utils ──

  const getRoleNameById = useCallback(
    (roleId: string): string => {
      const role = getRoleById(roleId);

      return isTruthy(role) && isTruthy(role.name)
        ? convertToTitleCase(role.name)
        : "-";
    },
    [getRoleById],
  );

  const getRoleOptionsForTab = useCallback(
    (tab: FundVehicleTab): ThemedDropdownOptionType[] =>
      getRoleOptionsForFundVehicleTab(uniqueRoles, tab),
    [uniqueRoles],
  );

  // ── Per-user view ──

  const buildUserStateView = useCallback(
    (userId: string): UserStateView => {
      const collaborator = collaboratorsById.get(userId);

      if (!collaborator) return createEmptyUserStateView();

      const state = userStates[userId] ?? createEmptyUserState();
      const { markedForDeletion, pendingCreations, editedPermissions } = state;

      const uniquePermissions = Array.from(
        (collaborator.permissions ?? [])
          .reduce((byKey, permission) => {
            if (!permission.roleId) return byKey;

            const key = getRowKeyFromPermission(permission);

            if (!byKey.has(key)) byKey.set(key, permission);

            return byKey;
          }, new Map<string, CollaboratorPermissionEntry>())
          .values(),
      );

      const pendingPermissions: CollaboratorPermissionEntry[] =
        pendingCreations.map((creation) => ({
          userId: collaborator.id,
          roleId: creation.roleId,
          manageable: true,
          roleSubjectType:
            FUND_VEHICLE_TAB_TO_SUBJECT_TYPE[creation.fundVehicleTab],
        }));

      const getEffectiveFundVehicleTab = (
        permission: CollaboratorPermissionEntry,
      ): FundVehicleTab => {
        const rowKey = getRowKeyFromPermission(permission);

        return rowKey in editedPermissions
          ? editedPermissions[rowKey].fundVehicleTab
          : getFundVehicleTabFromPermission(permission);
      };

      const permissionsData = [
        ...uniquePermissions,
        ...pendingPermissions,
      ].sort(
        (left, right) =>
          FUND_VEHICLE_ORDER[getEffectiveFundVehicleTab(left)] -
          FUND_VEHICLE_ORDER[getEffectiveFundVehicleTab(right)],
      );

      const hasChanges =
        markedForDeletion.size > 0 ||
        pendingCreations.length > 0 ||
        Object.keys(editedPermissions).length > 0;

      const hasInvalidEntries =
        Object.entries(editedPermissions).some(
          ([rowKey, edited]) =>
            !markedForDeletion.has(rowKey) && !edited.roleId,
        ) || pendingCreations.some((creation) => !creation.roleId);

      const getDisabledRoleIdsForTab = (
        tab: FundVehicleTab,
        excludeRowKey?: string,
      ): string[] =>
        permissionsData.reduce<string[]>((disabled, permission) => {
          const rowKey = getRowKeyFromPermission(permission);

          if (!rowKey || rowKey === excludeRowKey) return disabled;

          if (markedForDeletion.has(rowKey)) return disabled;

          if (getEffectiveFundVehicleTab(permission) !== tab) return disabled;

          const effectiveRoleId =
            rowKey in editedPermissions
              ? editedPermissions[rowKey].roleId
              : (permission.roleId ?? "");

          if (effectiveRoleId) disabled.push(effectiveRoleId);

          return disabled;
        }, []);

      const getPermissionsMarkedForDeletion = () =>
        uniquePermissions.filter((permission) =>
          markedForDeletion.has(getRowKeyFromPermission(permission)),
        );

      const cellState: CellState = {
        markedForDeletion,
        editedPermissions,
        pendingCreationRowKeys: new Set(
          pendingCreations.map((creation) =>
            getRowKey(creation.roleId, creation.fundVehicleTab),
          ),
        ),
        getRowKey: getRowKeyFromPermission,
        onToggleDeletion: (rowKey) => toggleDeletion(userId, rowKey),
        onRemoveCreation: (rowKey) => removeRole(userId, rowKey),
        onEditPermission: (rowKey, updates) =>
          editPermission(userId, rowKey, updates),
        getRoleNameById,
        getRoleOptionsForTab,
        getEffectiveFundVehicleTab,
        getDisabledRoleIdsForTab,
      };

      const readOnlyColumns: ColumnDef<CollaboratorPermissionEntry>[] = [
        {
          header: m.common_role(),
          accessorKey: "role",
          cell: ({ row }) => getRoleNameById(row.original.roleId as string),
        },
        {
          header: m.fund_vehicle(),
          accessorKey: "fundVehicle",
          cell: ({ row }) =>
            getFundVehicleTag(getFundVehicleTabFromPermission(row.original)),
        },
      ];

      const editableColumns: ColumnDef<CollaboratorPermissionEntry>[] = [
        {
          ...MAX_TEXT_COLUMN_PROPS,
          header: m.common_role(),
          accessorKey: "role",
          addCellTooltip: false,
          cell: ({ row }) => <EditableRoleCell row={row} state={cellState} />,
        },
        {
          ...MAX_TEXT_COLUMN_PROPS,
          header: m.fund_vehicle(),
          accessorKey: "fundVehicle",
          addCellTooltip: false,
          cell: ({ row }) => (
            <EditableFundVehicleCell row={row} state={cellState} />
          ),
        },
        {
          ...ACTION_COLUMN_DEFAULT_PROPS,
          id: "delete",
          className: "px-2 mx-0",
          cell: ({ row }) => <DeleteActionCell row={row} state={cellState} />,
        },
      ];

      return {
        permissionsData,
        columns: isEditing ? editableColumns : readOnlyColumns,
        collaboratorHasNoPermissions: uniquePermissions.length === 0,
        hasChanges,
        hasInvalidEntries,
        markedForDeletion,
        pendingCreations,
        editedPermissions,
        getDisabledRoleIdsForTab,
        getPermissionsMarkedForDeletion,
        addRole: (roleId, tab) => addRole(userId, roleId, tab),
        toggleDeletion: (rowKey) => toggleDeletion(userId, rowKey),
        unmarkForDeletion: (rowKey) => unmarkForDeletion(userId, rowKey),
        removeRole: (rowKey) => removeRole(userId, rowKey),
        editPermission: (rowKey, updates) =>
          editPermission(userId, rowKey, updates),
        resetChanges: () => resetChanges(userId),
      };
    },
    [
      addRole,
      collaboratorsById,
      editPermission,
      getRoleNameById,
      getRoleOptionsForTab,
      isEditing,
      removeRole,
      resetChanges,
      toggleDeletion,
      unmarkForDeletion,
      userStates,
    ],
  );

  const userStateViews = useMemo(() => {
    const views: Record<string, UserStateView> = {};
    collaborators.forEach((collaborator) => {
      views[collaborator.id] = buildUserStateView(collaborator.id);
    });

    return views;
  }, [buildUserStateView, collaborators]);

  const getUserState = useCallback(
    (userId: string): UserStateView =>
      userStateViews[userId] ?? buildUserStateView(userId),
    [buildUserStateView, userStateViews],
  );

  // ── Aggregates ──

  const hasAnyChanges = useMemo(
    () =>
      collaborators.some((collaborator) => {
        const state = userStates[collaborator.id];

        return (
          state &&
          (state.markedForDeletion.size > 0 ||
            state.pendingCreations.length > 0 ||
            Object.keys(state.editedPermissions).length > 0)
        );
      }),
    [collaborators, userStates],
  );

  const hasAnyInvalidEntries = useMemo(
    () =>
      collaborators.some((collaborator) => {
        const state = userStates[collaborator.id];

        if (!state) return false;

        return (
          Object.entries(state.editedPermissions).some(
            ([rowKey, edited]) =>
              !state.markedForDeletion.has(rowKey) && !edited.roleId,
          ) || state.pendingCreations.some((creation) => !creation.roleId)
        );
      }),
    [collaborators, userStates],
  );

  // ── Bulk save ──

  const saveOneUser = async (collaborator: CollaboratorDetails) => {
    const state = userStates[collaborator.id];

    if (!state) return;

    const { markedForDeletion, pendingCreations, editedPermissions } = state;

    const findOriginalPermissionByRowKey = (rowKey: string) =>
      collaborator.permissions?.find(
        (permission) => getRowKeyFromPermission(permission) === rowKey,
      );

    const deletionPromises = [...markedForDeletion].map((rowKey) => {
      const permission = findOriginalPermissionByRowKey(rowKey);

      return revokeRoleAsync({
        roleId: permission?.roleId ?? "",
        params: {
          userId: collaborator.id,
          subjectType: permission?.roleSubjectType,
          subjectId: permission?.roleSubjectId,
        },
      });
    });

    const editRevokePromises = Object.keys(editedPermissions)
      .filter((rowKey) => !markedForDeletion.has(rowKey))
      .map((rowKey) => {
        const permission = findOriginalPermissionByRowKey(rowKey);

        return revokeRoleAsync({
          roleId: permission?.roleId ?? "",
          params: {
            userId: collaborator.id,
            subjectType: permission?.roleSubjectType,
            subjectId: permission?.roleSubjectId,
          },
        });
      });

    const allRevokePromises = [...deletionPromises, ...editRevokePromises];

    if (allRevokePromises.length > 0) {
      await Promise.all(allRevokePromises);
    }

    const mutatedEntries = [
      ...pendingCreations.map((creation) => ({
        roleId: creation.roleId,
        subjectId: undefined,
        subjectType: FUND_VEHICLE_TAB_TO_SUBJECT_TYPE[creation.fundVehicleTab],
      })),
      ...Object.entries(editedPermissions).map(([rowKey, edited]) => {
        const originalPermission = findOriginalPermissionByRowKey(rowKey);

        return {
          roleId: edited.roleId,
          subjectId: originalPermission?.subjectId,
          subjectType: FUND_VEHICLE_TAB_TO_SUBJECT_TYPE[edited.fundVehicleTab],
        };
      }),
    ];

    if (mutatedEntries.length > 0) {
      await assignRolesAsync({
        userId: collaborator.id,
        data: { permissions: { entries: mutatedEntries } },
      });
    }
  };

  const save = async (callbacks?: BulkSaveCallbacks) => {
    if (collaborators.length === 0) return;

    const usersWithChanges = collaborators.filter((collaborator) => {
      const state = userStates[collaborator.id];

      return (
        state &&
        (state.markedForDeletion.size > 0 ||
          state.pendingCreations.length > 0 ||
          Object.keys(state.editedPermissions).length > 0)
      );
    });

    if (usersWithChanges.length === 0) {
      callbacks?.onSuccess?.();

      return;
    }

    const results = await Promise.allSettled(
      usersWithChanges.map((collaborator) => saveOneUser(collaborator)),
    );

    type FailedSave = { userId: string; error: ErrorType<unknown> };
    const failed = results.reduce<FailedSave[]>((failures, result, index) => {
      if (result.status === "rejected") {
        failures.push({
          userId: usersWithChanges[index].id,
          error: result.reason as ErrorType<unknown>,
        });
      }

      return failures;
    }, []);

    const succeededIds = new Set(
      results
        .map((result, index) =>
          result.status === "fulfilled" ? usersWithChanges[index].id : null,
        )
        .filter((id): id is string => id !== null),
    );
    setUserStates((previous) =>
      Object.fromEntries(
        Object.entries(previous).filter(([key]) => !succeededIds.has(key)),
      ),
    );
    setFailedUserIds(new Set(failed.map((failure) => failure.userId)));

    queryClient.invalidateQueries({ queryKey: getGetRolesNewQueryKey() });
    queryClient.invalidateQueries({
      queryKey: getGetWorkbenchCollaboratorsQueryKey(),
    });

    if (failed.length === 0) {
      callbacks?.onSuccess?.();
    } else if (failed.length < usersWithChanges.length) {
      callbacks?.onPartialFailure?.(failed);
    } else {
      callbacks?.onError?.(failed[0].error);
    }
  };

  const isSaving = isDeleting || isAssigning;

  return {
    getUserState,
    hasAnyChanges,
    hasAnyInvalidEntries,
    save,
    isSaving,
    failedUserIds,
    roles: uniqueRoles,
    isLoadingRoles,
    getRoleNameById,
    getRoleOptionsForTab,
    resetChanges,
    resetAll,
  };
};
